import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { map } from 'lodash';
import { Collapse } from 'reactstrap';
import { Input, Select, Button } from '../../components/base';
import { getPositions } from '../../states/thunks/positions.thunk';
import { getLevels, getUnitOptions } from '../../states/thunks/options.thunk';
import { saveRombelSettings } from '../../actions/settings.action';


class RombelSettingsForm extends Component {
  constructor(props) {
    super(props);

    const initialRule = {
      id: Date.now(), // For UI tracking only
      position_id: '',
      min_rombel: '',
      max_rombel: '',
      headcount: '',
    };

    this.state = {
      form: {
        value: {
          id: '',
          level_id: '',
          default_rules: [initialRule],
          unit_rules: [
            {
              id: Date.now(), // For UI tracking only
              unit_id: '',
              rules: [{ ...initialRule, id: Date.now() + 1 }],
            },
          ],
        },
        error: {},
      },
      param: {
        positions: [],
        levels: [],
        units: [],
      },
      isSubmitting: false,
      isUnitSettingsOpen: false,
    };

    this._onFormChange = this._onFormChange.bind(this);
    this._onSubmit = this._onSubmit.bind(this);
    this._addDefaultRule = this._addDefaultRule.bind(this);
    this._removeDefaultRule = this._removeDefaultRule.bind(this);
    this._onDefaultRuleChange = this._onDefaultRuleChange.bind(this);
    this._toggleUnitSettings = this._toggleUnitSettings.bind(this);
    this._addUnitRule = this._addUnitRule.bind(this);
    this._removeUnitRule = this._removeUnitRule.bind(this);
    this._onUnitChange = this._onUnitChange.bind(this);
    this._addUnitRuleRow = this._addUnitRuleRow.bind(this);
    this._removeUnitRuleRow = this._removeUnitRuleRow.bind(this);
    this._onUnitRuleRowChange = this._onUnitRuleRowChange.bind(this);
  }

  componentDidMount() {
    this._getPositionsOptions();
    this._getLevelsOptions();
    this._getUnitsOptions();

    // If editing, populate form with existing data
    if (this.props.location && this.props.location.state && this.props.location.state.rombel) {
      const { rombel } = this.props.location.state;

      // Transform the data to match our form structure
      const formValue = {
        id: rombel.id,
        level_id: rombel.level_id,
        default_rules: rombel.default_rules.map(rule => ({
          ...rule,
          id: Date.now() + Math.random(), // Add unique id for UI
        })),
        unit_rules: rombel.unit_rules.map(unitRule => ({
          id: Date.now() + Math.random(), // Add unique id for UI
          unit_id: unitRule.unit_id,
          rules: unitRule.rules.map(rule => ({
            ...rule,
            id: Date.now() + Math.random(), // Add unique id for UI
          })),
        })),
      };

      this.setState(prevState => ({
        form: {
          ...prevState.form,
          value: formValue,
        },
        isUnitSettingsOpen: rombel.unit_rules && rombel.unit_rules.length > 0,
      }));
    }
  }

  async _getPositionsOptions(filters = {}) {
    try {
      const { getPositionsOptions } = this.props;
      let positions = await getPositionsOptions(filters);

      // Ensure positions is an array
      if (!Array.isArray(positions)) {
        positions = [];
      }

      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          positions,
        },
      }));
    } catch (err) {
      // Silent error handling
      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          positions: [],
        },
      }));
    }
  }

  async _getLevelsOptions(filters = {}) {
    try {
      const { getLevelsOptions } = this.props;
      let levels = await getLevelsOptions(filters);

      // Ensure levels is an array
      if (!Array.isArray(levels)) {
        levels = [];
      }

      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          levels,
        },
      }));
    } catch (err) {
      // Silent error handling
      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          levels: [],
        },
      }));
    }
  }

  async _getUnitsOptions(filters = {}) {
    try {
      const { getUnitsOptions } = this.props;
      let units = await getUnitsOptions(filters);

      // Ensure units is an array
      if (!Array.isArray(units)) {
        units = [];
      }

      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          units,
        },
      }));
    } catch (err) {
      // Silent error handling
      this.setState(prevState => ({
        ...prevState,
        param: {
          ...prevState.param,
          units: [],
        },
      }));
    }
  }

  _onFormChange(e) {
    const { name, value } = e.target;
    this.setState(prevState => ({
      form: {
        ...prevState.form,
        value: {
          ...prevState.form.value,
          [name]: value,
        },
        error: {
          ...prevState.form.error,
          [name]: '',
        },
      },
    }));
  }

  _onDefaultRuleChange(e, ruleId) {
    const { name, value } = e.target;

    this.setState((prevState) => {
      const updatedRules = prevState.form.value.default_rules.map((rule) => {
        if (rule.id === ruleId) {
          return { ...rule, [name]: value };
        }
        return rule;
      });

      return {
        form: {
          ...prevState.form,
          value: {
            ...prevState.form.value,
            default_rules: updatedRules,
          },
          error: {
            ...prevState.form.error,
            [`default_rules.${ruleId}.${name}`]: '',
          },
        },
      };
    });
  }

  _onUnitChange(e, unitRuleId) {
    const { name, value } = e.target;

    this.setState(prevState => ({
      form: {
        ...prevState.form,
        value: {
          ...prevState.form.value,
          unit_rules: prevState.form.value.unit_rules.map((unitRule) => {
            if (unitRule.id === unitRuleId) {
              return {
                ...unitRule,
                [name]: value,
              };
            }
            return unitRule;
          }),
        },
        error: {
          ...prevState.form.error,
          [`unit_rules.${unitRuleId}.${name}`]: '',
        },
      },
    }));
  }

  _onUnitRuleRowChange(e, unitRuleId, ruleId) {
    const { name, value } = e.target;

    this.setState((prevState) => {
      const updatedUnitRules = prevState.form.value.unit_rules.map((unitRule) => {
        if (unitRule.id === unitRuleId) {
          const updatedRules = unitRule.rules.map((rule) => {
            if (rule.id === ruleId) {
              return { ...rule, [name]: value };
            }
            return rule;
          });

          return {
            ...unitRule,
            rules: updatedRules,
          };
        }
        return unitRule;
      });

      return {
        form: {
          ...prevState.form,
          value: {
            ...prevState.form.value,
            unit_rules: updatedUnitRules,
          },
          error: {
            ...prevState.form.error,
            [`unit_rules.${unitRuleId}.rules.${ruleId}.${name}`]: '',
          },
        },
      };
    });
  }

  _addDefaultRule() {
    this.setState(prevState => ({
      form: {
        ...prevState.form,
        value: {
          ...prevState.form.value,
          default_rules: [
            ...prevState.form.value.default_rules,
            {
              id: Date.now(),
              position_id: '',
              min_rombel: '',
              max_rombel: '',
              headcount: '',
            },
          ],
        },
      },
    }));
  }

  _removeDefaultRule(ruleId) {
    this.setState((prevState) => {
      // Don't remove if it's the last rule
      if (prevState.form.value.default_rules.length <= 1) {
        return prevState;
      }

      return {
        form: {
          ...prevState.form,
          value: {
            ...prevState.form.value,
            default_rules: prevState.form.value.default_rules.filter(rule => rule.id !== ruleId),
          },
        },
      };
    });
  }

  _addUnitRule() {
    const initialRule = {
      id: Date.now() + 2,
      position_id: '',
      min_rombel: '',
      max_rombel: '',
      headcount: '',
    };

    this.setState(prevState => ({
      form: {
        ...prevState.form,
        value: {
          ...prevState.form.value,
          unit_rules: [
            ...prevState.form.value.unit_rules,
            {
              id: Date.now(),
              unit_id: '',
              rules: [initialRule],
            },
          ],
        },
      },
    }));
  }

  _removeUnitRule(unitRuleId) {
    this.setState((prevState) => {
      // Don't remove if it's the last unit rule
      if (prevState.form.value.unit_rules.length <= 1) {
        return prevState;
      }

      return {
        form: {
          ...prevState.form,
          value: {
            ...prevState.form.value,
            unit_rules: prevState.form.value.unit_rules.filter(unitRule => unitRule.id !== unitRuleId),
          },
        },
      };
    });
  }

  _addUnitRuleRow(unitRuleId) {
    this.setState(prevState => ({
      form: {
        ...prevState.form,
        value: {
          ...prevState.form.value,
          unit_rules: prevState.form.value.unit_rules.map((unitRule) => {
            if (unitRule.id === unitRuleId) {
              return {
                ...unitRule,
                rules: [
                  ...unitRule.rules,
                  {
                    id: Date.now(),
                    position_id: '',
                    min_rombel: '',
                    max_rombel: '',
                    headcount: '',
                  },
                ],
              };
            }
            return unitRule;
          }),
        },
      },
    }));
  }

  _removeUnitRuleRow(unitRuleId, ruleId) {
    this.setState((prevState) => {
      const updatedUnitRules = prevState.form.value.unit_rules.map((unitRule) => {
        if (unitRule.id === unitRuleId) {
          // Don't remove if it's the last rule
          if (unitRule.rules.length <= 1) {
            return unitRule;
          }

          return {
            ...unitRule,
            rules: unitRule.rules.filter(rule => rule.id !== ruleId),
          };
        }
        return unitRule;
      });

      return {
        form: {
          ...prevState.form,
          value: {
            ...prevState.form.value,
            unit_rules: updatedUnitRules,
          },
        },
      };
    });
  }

  _toggleUnitSettings() {
    this.setState(prevState => ({
      isUnitSettingsOpen: !prevState.isUnitSettingsOpen,
    }));
  }

  async _onSubmit(e) {
    e.preventDefault();

    const { form } = this.state;
    const { saveRombelSettings, history } = this.props;
    const isEditing = !!form.value.id;

    // Validate form
    let hasError = false;
    const errors = {};

    // Check level_id
    if (!form.value.level_id) {
      errors.level_id = 'Level harus diisi';
      hasError = true;
    }

    // Check default rules
    form.value.default_rules.forEach((rule, index) => {
      if (!rule.position_id) {
        errors[`default_rules.${rule.id}.position_id`] = 'Posisi harus diisi';
        hasError = true;
      }
      if (!rule.min_rombel) {
        errors[`default_rules.${rule.id}.min_rombel`] = 'Min rombel harus diisi';
        hasError = true;
      }
      if (!rule.max_rombel) {
        errors[`default_rules.${rule.id}.max_rombel`] = 'Max rombel harus diisi';
        hasError = true;
      }
      if (!rule.headcount) {
        errors[`default_rules.${rule.id}.headcount`] = 'Jumlah orang harus diisi';
        hasError = true;
      }
    });

    // Check unit rules if open
    if (this.state.isUnitSettingsOpen) {
      form.value.unit_rules.forEach((unitRule) => {
        if (!unitRule.unit_id) {
          errors[`unit_rules.${unitRule.id}.unit_id`] = 'Unit harus diisi';
          hasError = true;
        }

        unitRule.rules.forEach((rule) => {
          if (!rule.position_id) {
            errors[`unit_rules.${unitRule.id}.rules.${rule.id}.position_id`] = 'Posisi harus diisi';
            hasError = true;
          }
          if (!rule.min_rombel) {
            errors[`unit_rules.${unitRule.id}.rules.${rule.id}.min_rombel`] = 'Min rombel harus diisi';
            hasError = true;
          }
          if (!rule.max_rombel) {
            errors[`unit_rules.${unitRule.id}.rules.${rule.id}.max_rombel`] = 'Max rombel harus diisi';
            hasError = true;
          }
          if (!rule.headcount) {
            errors[`unit_rules.${unitRule.id}.rules.${rule.id}.headcount`] = 'Jumlah orang harus diisi';
            hasError = true;
          }
        });
      });
    }

    if (hasError) {
      this.setState(prevState => ({
        form: {
          ...prevState.form,
          error: errors,
        },
      }));
      return;
    }

    // Prepare data for submission
    const formData = {
      id: form.value.id,
      level_id: form.value.level_id,
      default_rules: form.value.default_rules.map(rule => ({
        position_id: rule.position_id,
        min_rombel: rule.min_rombel,
        max_rombel: rule.max_rombel,
        headcount: rule.headcount,
      })),
      unit_rules: this.state.isUnitSettingsOpen
        ? form.value.unit_rules.map(unitRule => ({
          unit_id: unitRule.unit_id,
          rules: unitRule.rules.map(rule => ({
            position_id: rule.position_id,
            min_rombel: rule.min_rombel,
            max_rombel: rule.max_rombel,
            headcount: rule.headcount,
          })),
        }))
        : [],
    };

    try {
      this.setState({ isSubmitting: true });
      await saveRombelSettings(formData, isEditing);
      history.push('/dashboard/settings/rombel');
    } catch (error) {
      console.error('Error saving rombel settings:', error);
      // Handle error
    } finally {
      this.setState({ isSubmitting: false });
    }
  }

  render() {
    const {
      form, param, isSubmitting, isUnitSettingsOpen,
    } = this.state;
    const isEditing = !!form.value.id;

    return (
      <div className="rombel-settings-form">
        <h2>{isEditing ? 'Edit Pengaturan Rombel' : 'Tambah Pengaturan Rombel'}</h2>

        <form onSubmit={this._onSubmit}>
          <div className="form-group">
            <Select
              name="level_id"
              label="Level"
              placeholder="Pilih Level"
              data={param.levels}
              onChange={this._onFormChange}
              value={form.value.level_id}
              error={form.error.level_id || ''}
            />
          </div>

          <div className="rombel-settings-form__section">
            <h3>Pengaturan Default</h3>
            <table className="rombel-settings-form__table">
              <thead>
                <tr>
                  <th>Posisi</th>
                  <th>Min Rombel</th>
                  <th>Max Rombel</th>
                  <th>Jumlah Orang</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {map(form.value.default_rules, rule => (
                  <tr key={`default_rule_${rule.id}`}>
                    <td>
                      <Select
                        name="position_id"
                        onChange={e => this._onDefaultRuleChange(e, rule.id)}
                        data={param.positions}
                        placeholder="Pilih Posisi"
                        value={rule.position_id}
                        error={form.error[`default_rules.${rule.id}.position_id`] || ''}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="min_rombel"
                        onChange={e => this._onDefaultRuleChange(e, rule.id)}
                        value={rule.min_rombel}
                        error={form.error[`default_rules.${rule.id}.min_rombel`] || ''}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="max_rombel"
                        onChange={e => this._onDefaultRuleChange(e, rule.id)}
                        value={rule.max_rombel}
                        error={form.error[`default_rules.${rule.id}.max_rombel`] || ''}
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        name="headcount"
                        onChange={e => this._onDefaultRuleChange(e, rule.id)}
                        value={rule.headcount}
                        error={form.error[`default_rules.${rule.id}.headcount`] || ''}
                      />
                    </td>
                    <td>
                      <Button
                        title="Hapus"
                        onClick={() => this._removeDefaultRule(rule.id)}
                        className="button--small button--danger"
                        type="button"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Button
              title="Tambah Baris"
              onClick={this._addDefaultRule}
              className="button--small"
              type="button"
            />
          </div>

          <div className="rombel-settings-form__section">
            <div className="rombel-settings-form__toggle">
              <Button
                title={isUnitSettingsOpen ? 'Sembunyikan Pengaturan Unit' : 'Tampilkan Pengaturan Unit'}
                onClick={this._toggleUnitSettings}
                className="button--secondary button--small"
                type="button"
              />
            </div>

            <Collapse isOpen={isUnitSettingsOpen}>
              <h3>Pengaturan Per Unit</h3>

              {map(form.value.unit_rules, (unitRule, unitIndex) => (
                <div key={`unit_rule_${unitRule.id}`} className="rombel-settings-form__unit-section">
                  <div className="rombel-settings-form__unit-header">
                    <div className="form-group">
                      <Select
                        name="unit_id"
                        label={`Unit ${unitIndex + 1}`}
                        placeholder="Pilih Unit"
                        data={param.units}
                        onChange={e => this._onUnitChange(e, unitRule.id)}
                        value={unitRule.unit_id}
                        error={form.error[`unit_rules.${unitRule.id}.unit_id`] || ''}
                      />
                    </div>

                    <Button
                      title="Hapus Unit"
                      onClick={() => this._removeUnitRule(unitRule.id)}
                      className="button--small button--danger"
                      type="button"
                    />
                  </div>

                  <table className="rombel-settings-form__table">
                    <thead>
                      <tr>
                        <th>Posisi</th>
                        <th>Min Rombel</th>
                        <th>Max Rombel</th>
                        <th>Jumlah Orang</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {map(unitRule.rules, rule => (
                        <tr key={`unit_rule_${unitRule.id}_rule_${rule.id}`}>
                          <td>
                            <Select
                              name="position_id"
                              onChange={e => this._onUnitRuleRowChange(e, unitRule.id, rule.id)}
                              data={param.positions}
                              placeholder="Pilih Posisi"
                              value={rule.position_id}
                              error={form.error[`unit_rules.${unitRule.id}.rules.${rule.id}.position_id`] || ''}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              name="min_rombel"
                              onChange={e => this._onUnitRuleRowChange(e, unitRule.id, rule.id)}
                              value={rule.min_rombel}
                              error={form.error[`unit_rules.${unitRule.id}.rules.${rule.id}.min_rombel`] || ''}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              name="max_rombel"
                              onChange={e => this._onUnitRuleRowChange(e, unitRule.id, rule.id)}
                              value={rule.max_rombel}
                              error={form.error[`unit_rules.${unitRule.id}.rules.${rule.id}.max_rombel`] || ''}
                            />
                          </td>
                          <td>
                            <Input
                              type="number"
                              name="headcount"
                              onChange={e => this._onUnitRuleRowChange(e, unitRule.id, rule.id)}
                              value={rule.headcount}
                              error={form.error[`unit_rules.${unitRule.id}.rules.${rule.id}.headcount`] || ''}
                            />
                          </td>
                          <td>
                            <Button
                              title="Hapus"
                              onClick={() => this._removeUnitRuleRow(unitRule.id, rule.id)}
                              className="button--small button--danger"
                              type="button"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <Button
                    title="Tambah Baris"
                    onClick={() => this._addUnitRuleRow(unitRule.id)}
                    className="button--small"
                    type="button"
                  />
                </div>
              ))}

              <div className="rombel-settings-form__add-unit">
                <Button
                  title="Tambah Unit"
                  onClick={this._addUnitRule}
                  className="button--small button--secondary"
                  type="button"
                />
              </div>
            </Collapse>
          </div>

          <div className="rombel-settings-form__actions">
            <Button
              title="Kembali"
              onClick={() => this.props.history.push('/dashboard/settings/rombel')}
              className="button--secondary"
              type="button"
            />
            <Button
              title={isSubmitting ? 'Menyimpan...' : 'Simpan'}
              disabled={isSubmitting}
              type="submit"
            />
          </div>
        </form>
      </div>
    );
  }
}

RombelSettingsForm.propTypes = {
  getPositionsOptions: PropTypes.func.isRequired,
  getLevelsOptions: PropTypes.func.isRequired,
  getUnitsOptions: PropTypes.func.isRequired,
  saveRombelSettings: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object,
};

RombelSettingsForm.defaultProps = {
  location: {},
};

const mapDispatchToProps = dispatch => ({
  getPositionsOptions: filters => dispatch(getPositions(filters)),
  getLevelsOptions: filters => dispatch(getLevels(filters)),
  getUnitsOptions: filters => dispatch(getUnitOptions(filters)),
  saveRombelSettings: (data, callback) => dispatch(saveRombelSettings(data, callback)),
});

export default connect(null, mapDispatchToProps)(RombelSettingsForm);
